import React, { useCallback, useMemo } from 'react';
import { eWeekdays, IAdminWorkSchedule } from '../../types/userProfile/IUserProfile';
import { checkForMutationSuccess } from '../../API/checkForMutationSuccess';
import { useUpdateSelfAdminUserMutation } from '../../API/administration/AdminUsersAPI';
import { Form, Formik } from 'formik';
import { SelfUpdateAdminRequest } from '../../types/userProfile/SelfUpdateAdminRequest';
import { editProfilePageForm } from '../../validation/userProfile/ProfilePageForm';
import { TextFieldTW } from '../../tailwindComponents/textInputTW/TextFieldTW';
import { LocationTimeZoneSelect } from './formElements/LocationTimeZoneSelect';
import { PhoneNumberFieldTW } from '../../tailwindComponents/phoneNumberFieldTW/PhoneNumberFieldTW';
import { NumberField } from '../../ui-toolkit';
import { LanguagesSelect } from './formElements/LanguagesSelect';
import { OrderContactNotesField } from '../order/contacts/subComponents/OrderContactNotesField';
import { WorkScheduleFields } from './formElements/WorkScheduleFields';
import { ErrorMessageTW } from '../../tailwindComponents/errorMessage/ErrorMessageTW';
import { SubmissionFooter } from './formElements/SubmissionFooter';
import { LinkToMyOrders } from './LinkToMyOrders';
import { useInitialSelfUpdateFormValues } from './hooks/useInitialSelfUpdateFormValues';
import { useHasPermissions } from '../accessControl/hooks/useHasPermissions';
import { ePermissions } from '../../types/administration/userAdministration/ePermissions';

const cleanObject = (formikState: SelfUpdateAdminRequest): SelfUpdateAdminRequest => {
  const workSchedule: Partial<Record<eWeekdays, IAdminWorkSchedule>> = {};
  Object.entries(formikState.workSchedule)
    .filter(([, { startTime, endTime }]) => {
      // Include entries that have at least one time field filled and are valid (no underscores)
      const hasValidStartTime = startTime && startTime.trim() !== '' && startTime.indexOf('_') === -1;
      const hasValidEndTime = endTime && endTime.trim() !== '' && endTime.indexOf('_') === -1;
      return hasValidStartTime || hasValidEndTime;
    })
    .forEach(([workday, adminWorkSchedule]) => {
      // Clean invalid time values (with underscores) before sending to backend
      workSchedule[workday as eWeekdays] = {
        startTime: adminWorkSchedule.startTime && adminWorkSchedule.startTime.indexOf('_') === -1
          ? adminWorkSchedule.startTime
          : '',
        endTime: adminWorkSchedule.endTime && adminWorkSchedule.endTime.indexOf('_') === -1
          ? adminWorkSchedule.endTime
          : '',
      };
    });
  return {
    phoneNumber: formikState.phoneNumber || null,
    phoneExtension: formikState.phoneExtension || null,
    externalEmail: formikState.externalEmail?.trim() || null,
    timeZone: formikState.timeZone || null,
    notes: formikState.notes || null,
    pronouns: formikState.pronouns || null,
    languages: formikState.languages,
    workSchedule,
  };
};

export const EditSelfForm: React.FC<{
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
}> = ({ setOpen }) => {
  const [updateSelf, { error, isLoading }] = useUpdateSelfAdminUserMutation();
  const canEditExternalEmail = useHasPermissions([ePermissions.EditExternalEmail]);

  const handleUpdateSubmit = useCallback(
    async (values: SelfUpdateAdminRequest) => {
      const cleanedValues = cleanObject(values);
      const response = await updateSelf(cleanedValues);
      if (checkForMutationSuccess(response, 'updating my profile')) {
        setOpen(false);
      }
    },
    [updateSelf, setOpen]
  );
  const initialValues = useInitialSelfUpdateFormValues();

  const validationSchema = useMemo(
    () => editProfilePageForm(canEditExternalEmail),
    [canEditExternalEmail]
  );

  return (
    <>
      <ErrorMessageTW rtkError={error} />
      <Formik
        initialValues={initialValues}
        onSubmit={handleUpdateSubmit}
        validationSchema={validationSchema}
        enableReinitialize
      >
        <Form>
          <div className={'flex flex-col gap-2'}>
            <TextFieldTW name={'pronouns'} labelText={'Pronouns'} />
            <LocationTimeZoneSelect />
            <PhoneNumberFieldTW name={'phoneNumber'} labelText={'Phone Number'} />
            <NumberField name={'phoneExtension'} label={'Phone Extension'} />
            {canEditExternalEmail && (
              <TextFieldTW name={'externalEmail'} labelText={'External Email'} />
            )}
            <WorkScheduleFields />
            <LanguagesSelect />
            <OrderContactNotesField name={'notes'} />
            <LinkToMyOrders setOpen={setOpen} />
            <SubmissionFooter onCancel={() => setOpen(false)} disabled={isLoading} />
          </div>
        </Form>
      </Formik>
    </>
  );
};
